import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import { AdminAuditLogFilters, AdminAuditLogItem } from '../types';

const AUDIT_LIMIT = 50;

const INITIAL_FILTERS: Required<Omit<AdminAuditLogFilters, 'limit'>> = {
  adminSearch: '',
  actionType: '',
  targetSearch: '',
  from: '',
  to: '',
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const truncateText = (value?: string | null, maxLength = 72): string => {
  const normalized = String(value || '').trim();
  if (!normalized) return '-';
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3)}...`;
};

const AuditPage: React.FC = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [logs, setLogs] = useState<AdminAuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async (nextFilters: typeof INITIAL_FILTERS) => {
    setLoading(true);
    setError('');

    try {
      const payload = await adminService.listAuditLogs({
        ...nextFilters,
        limit: AUDIT_LIMIT,
      });
      setLogs(payload.items || []);
      setTotal(payload.total || 0);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar a trilha administrativa');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData(INITIAL_FILTERS);
  }, [loadData]);

  const metricCards = useMemo(
    () => [
      {
        eyebrow: 'Audit',
        title: 'Eventos encontrados',
        value: String(total),
        tone: 'emerald' as const,
      },
      {
        eyebrow: 'Lista',
        title: 'Eventos carregados',
        value: String(logs.length),
        tone: 'blue' as const,
      },
      {
        eyebrow: 'Acoes',
        title: 'Tipos distintos',
        value: String(new Set(logs.map((log) => log.action_type)).size),
        tone: 'amber' as const,
      },
      {
        eyebrow: 'Governanca',
        title: 'Com motivo',
        value: String(logs.filter((log) => Boolean(log.reason)).length),
        tone: 'slate' as const,
      },
    ],
    [logs, total]
  );

  const handleFilterChange = (field: keyof typeof INITIAL_FILTERS, value: string) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadData(filters);
  };

  const handleClear = () => {
    setFilters(INITIAL_FILTERS);
    void loadData(INITIAL_FILTERS);
  };

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Audit</p>
          <h1>Trilha administrativa</h1>
          <p>
            Leitura central da atividade administrativa da plataforma. Nesta etapa o modulo nasce
            em modo seguro, apenas para consulta.
          </p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => void loadData(filters)} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Filtros</p>
            <h2>Consulta operacional</h2>
          </div>
          <span className="admin-chip">Limite atual: {AUDIT_LIMIT} eventos</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-filter-grid">
            <label className="admin-filter-field">
              <span>Admin</span>
              <input
                className="admin-search-input"
                value={filters.adminSearch}
                onChange={(event) => handleFilterChange('adminSearch', event.target.value)}
                placeholder="Nome, email ou ID"
              />
            </label>

            <label className="admin-filter-field">
              <span>Acao</span>
              <input
                className="admin-search-input"
                value={filters.actionType}
                onChange={(event) => handleFilterChange('actionType', event.target.value)}
                placeholder="Ex.: CLUB_SUSPEND"
              />
            </label>

            <label className="admin-filter-field">
              <span>Alvo</span>
              <input
                className="admin-search-input"
                value={filters.targetSearch}
                onChange={(event) => handleFilterChange('targetSearch', event.target.value)}
                placeholder="Tipo ou ID do alvo"
              />
            </label>

            <label className="admin-filter-field">
              <span>De</span>
              <input
                type="date"
                className="admin-search-input"
                value={filters.from}
                onChange={(event) => handleFilterChange('from', event.target.value)}
              />
            </label>

            <label className="admin-filter-field">
              <span>Ate</span>
              <input
                type="date"
                className="admin-search-input"
                value={filters.to}
                onChange={(event) => handleFilterChange('to', event.target.value)}
              />
            </label>
          </div>

          <div className="admin-filter-actions">
            <button type="button" className="admin-secondary-button" onClick={handleClear} disabled={loading}>
              Limpar
            </button>
            <button type="submit" className="admin-secondary-button" disabled={loading}>
              {loading ? 'Buscando...' : 'Aplicar filtros'}
            </button>
          </div>
        </form>
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Estado da etapa</p>
            <h2>Leitura liberada, mutacao sensivel ainda bloqueada</h2>
          </div>
          <span className="admin-chip">{loading ? 'Sincronizando' : 'Consulta pronta'}</span>
        </div>
        <div className="admin-note-stack">
          <div className="admin-note-chip">A trilha nasce primeiro em modo leitura.</div>
          <div className="admin-note-chip">Acoes irreversiveis continuam fora de escopo ate a governanca amadurecer.</div>
          <div className="admin-note-chip">Quando nao houver eventos, o estado vazio tambem faz parte da resposta real do sistema.</div>
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Timeline</p>
            <h2>Eventos administrativos mais recentes</h2>
          </div>
          <span className="admin-chip">{total} evento(s) no total</span>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Admin</th>
                <th>Acao</th>
                <th>Alvo</th>
                <th>Motivo</th>
                <th>Origem</th>
              </tr>
            </thead>
            <tbody>
              {logs.length ? (
                logs.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <strong>{formatDateTime(entry.created_at)}</strong>
                      <span>{entry.id}</span>
                    </td>
                    <td>
                      <strong>{entry.admin_name}</strong>
                      <span>{entry.admin_email || entry.admin_user_id}</span>
                    </td>
                    <td>
                      <strong>{entry.action_type}</strong>
                      <span>Before: {entry.before_json ? 'sim' : 'nao'} . After: {entry.after_json ? 'sim' : 'nao'}</span>
                    </td>
                    <td>
                      <strong>{entry.target_type}</strong>
                      <span>{entry.target_id || 'Global'}</span>
                    </td>
                    <td>{entry.reason || '-'}</td>
                    <td>
                      <strong>{entry.ip_address || '-'}</strong>
                      <span>{truncateText(entry.user_agent)}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    Nenhum evento administrativo encontrado ainda para os filtros aplicados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AuditPage;
